import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  API_ENDPOINTS,
  API_SCOPES,
  createApiKey,
  createWebhook,
  deleteApiKey,
  deleteWebhook,
  listApiKeys,
  listWebhooks,
  methodClass,
  revokeApiKey,
  updateWebhook,
  type CoreApiKey,
  type CoreWebhook,
} from '@/lib/coreApi';
import { listEventTypes, type CoreEventType } from '@/lib/coreEvents';
import Modal from '@/pages/dashboard/core/components/Modal';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function ApiTab({ org }: Props) {
  const [keys, setKeys] = useState<CoreApiKey[]>([]);
  const [hooks, setHooks] = useState<CoreWebhook[]>([]);
  const [events, setEvents] = useState<CoreEventType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [keyOpen, setKeyOpen] = useState(false);
  const [keyForm, setKeyForm] = useState({ name: '', scopes: ['read'] as string[], rateLimit: 60 });
  const [newKey, setNewKey] = useState('');
  const [hookOpen, setHookOpen] = useState(false);
  const [hookForm, setHookForm] = useState({ name: '', url: '', events: [] as string[] });
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [k, h, e] = await Promise.all([listApiKeys(org.id), listWebhooks(org.id), listEventTypes()]);
      setKeys(k);
      setHooks(h);
      setEvents(e);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const saveKey = async () => {
    if (!keyForm.name.trim()) return;
    setSaving(true);
    try {
      const created = await createApiKey({
        tenantId: org.tenant_id,
        organizationId: org.id,
        name: keyForm.name.trim(),
        scopes: keyForm.scopes,
        rateLimit: keyForm.rateLimit,
      });
      setNewKey(created?.fullKey || '');
      setKeyOpen(false);
      setKeyForm({ name: '', scopes: ['read'], rateLimit: 60 });
      load();
    } finally {
      setSaving(false);
    }
  };

  const removeKey = async (k: CoreApiKey) => {
    setBusyId(k.id);
    try { await deleteApiKey(k.id); load(); } finally { setBusyId(null); }
  };

  const revoke = async (k: CoreApiKey) => {
    setBusyId(k.id);
    try { await revokeApiKey(k.id); load(); } finally { setBusyId(null); }
  };

  const saveHook = async () => {
    if (!hookForm.name.trim() || !hookForm.url.trim()) return;
    setSaving(true);
    try {
      await createWebhook({ tenantId: org.tenant_id, organizationId: org.id, name: hookForm.name.trim(), url: hookForm.url.trim(), events: hookForm.events });
      setHookOpen(false);
      setHookForm({ name: '', url: '', events: [] });
      load();
    } finally {
      setSaving(false);
    }
  };

  const toggleHook = async (h: CoreWebhook) => {
    setBusyId(h.id);
    try { await updateWebhook(h.id, { status: h.status === 'active' ? 'paused' : 'active' }); load(); } finally { setBusyId(null); }
  };

  const removeHook = async (h: CoreWebhook) => {
    setBusyId(h.id);
    try { await deleteWebhook(h.id); load(); } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        API-first : Zifek Core expose des APIs internes et externes structurées (REST aujourd’hui, GraphQL plus tard), des webhooks et des événements.
        Chaque appel utilise <span className="font-semibold text-foreground-700">authentication, authorization, validation, rate limiting et logging</span>.
      </p>

      {newKey && (
        <div className="p-4 bg-primary-50 border border-primary-200 rounded-lg">
          <p className="text-xs font-semibold text-primary-800 flex items-center gap-2"><i className="ri-key-2-line"></i> Votre nouvelle clé d’API — copiez-la maintenant, elle ne s’affichera plus.</p>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 px-3 py-2 rounded-md bg-background-50 border border-background-200/70 text-xs text-foreground-900 break-all">{newKey}</code>
            <button onClick={() => navigator.clipboard?.writeText(newKey)} className="px-3 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">Copier</button>
            <button onClick={() => setNewKey('')} className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"><i className="ri-close-line"></i></button>
          </div>
        </div>
      )}

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : (
        <>
          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-background-200/60 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2"><span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-key-2-line"></i></span><h4 className="text-sm font-semibold text-foreground-900">Clés d’API</h4></div>
              <button onClick={() => setKeyOpen(true)} className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-700 text-xs font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer"><i className="ri-add-line"></i> Nouvelle clé</button>
            </div>
            {keys.length === 0 ? (
              <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucune clé. Créez-en une pour autoriser vos intégrations externes.</p>
            ) : (
              <div className="divide-y divide-background-200/60">
                {keys.map((k) => (
                  <div key={k.id} className="flex items-center gap-3 px-4 py-3">
                    <i className="ri-key-line text-foreground-500"></i>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground-800 truncate">{k.name}</p>
                      <p className="text-[11px] text-foreground-400 truncate">{k.key_prefix}•••••••• · {k.rate_limit_per_min || 0} req/min</p>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${k.status === 'active' ? 'bg-primary-100 text-primary-900' : 'bg-background-100 text-foreground-500'}`}>{k.status}</span>
                    {k.status === 'active' && <button onClick={() => revoke(k)} disabled={busyId === k.id} className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer disabled:opacity-50">Révoquer</button>}
                    <button onClick={() => removeKey(k)} disabled={busyId === k.id} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"><i className="ri-delete-bin-line"></i></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-background-200/60 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2"><span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-webhook-line"></i></span><h4 className="text-sm font-semibold text-foreground-900">Webhooks</h4></div>
              <button onClick={() => setHookOpen(true)} className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-700 text-xs font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer"><i className="ri-add-line"></i> Nouveau webhook</button>
            </div>
            {hooks.length === 0 ? (
              <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucun webhook. Recevez les événements du socle sur vos endpoints.</p>
            ) : (
              <div className="divide-y divide-background-200/60">
                {hooks.map((h) => (
                  <div key={h.id} className="flex items-center gap-3 px-4 py-3">
                    <i className="ri-webhook-line text-foreground-500"></i>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground-800 truncate">{h.name}</p>
                      <p className="text-[11px] text-foreground-400 truncate">{h.url} · {(h.events || []).length} événement(s)</p>
                    </div>
                    <button onClick={() => toggleHook(h)} disabled={busyId === h.id} className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${h.status === 'active' ? 'bg-primary-500' : 'bg-background-200'}`}><span className={`absolute top-0.5 w-5 h-5 rounded-full bg-background-50 transition-all ${h.status === 'active' ? 'left-[22px]' : 'left-0.5'}`}></span></button>
                    <button onClick={() => removeHook(h)} disabled={busyId === h.id} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"><i className="ri-delete-bin-line"></i></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2"><span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center"><i className="ri-code-s-slash-line"></i></span><h4 className="text-sm font-semibold text-foreground-900">Endpoints REST</h4></div>
            <div className="divide-y divide-background-200/60">
              {API_ENDPOINTS.map((ep) => (
                <div key={`${ep.method}-${ep.path}`} className="flex items-center gap-3 px-4 py-3">
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-md whitespace-nowrap ${methodClass(ep.method)}`}>{ep.method}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-foreground-800 truncate">{ep.label}</p>
                    <code className="text-[11px] text-foreground-400 truncate">{ep.path}</code>
                  </div>
                  {ep.requiresAuth && <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 whitespace-nowrap"><i className="ri-lock-line"></i> auth</span>}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {keyOpen && (
        <Modal
          title="Nouvelle clé d’API"
          onClose={() => setKeyOpen(false)}
          maxWidth="max-w-lg"
          footer={
            <>
              <button onClick={() => setKeyOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={saveKey} disabled={saving || !keyForm.name.trim()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>} Générer
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Nom</span>
              <input className={INPUT} value={keyForm.name} onChange={(e) => setKeyForm({ ...keyForm, name: e.target.value })} placeholder="Ex : Intégration ERP" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Limite de débit (requêtes / minute)</span>
              <input type="number" className={INPUT} value={keyForm.rateLimit} onChange={(e) => setKeyForm({ ...keyForm, rateLimit: Number(e.target.value) || 60 })} min={1} />
            </label>
            <div>
              <span className="block text-xs font-medium text-foreground-600 mb-1">Portées</span>
              <div className="flex flex-wrap gap-2">
                {API_SCOPES.map((s) => {
                  const on = keyForm.scopes.includes(s.key);
                  return (
                    <button key={s.key} type="button" onClick={() => setKeyForm({ ...keyForm, scopes: on ? keyForm.scopes.filter((x) => x !== s.key) : [...keyForm.scopes, s.key] })} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors cursor-pointer ${on ? 'bg-primary-500 text-background-50 border-primary-500' : 'bg-background-50 text-foreground-600 border-background-200/70 hover:bg-background-100'}`}>
                      {on && <i className="ri-check-line"></i>}{s.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {hookOpen && (
        <Modal
          title="Nouveau webhook"
          onClose={() => setHookOpen(false)}
          maxWidth="max-w-lg"
          footer={
            <>
              <button onClick={() => setHookOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={saveHook} disabled={saving || !hookForm.name.trim() || !hookForm.url.trim()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>} Créer
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Nom</span>
              <input className={INPUT} value={hookForm.name} onChange={(e) => setHookForm({ ...hookForm, name: e.target.value })} placeholder="Ex : Notifier mon CRM" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">URL de destination</span>
              <input className={INPUT} value={hookForm.url} onChange={(e) => setHookForm({ ...hookForm, url: e.target.value })} placeholder="https://mon-app.com/webhooks/zifek" />
            </label>
            <div>
              <span className="block text-xs font-medium text-foreground-600 mb-1">Événements écoutés</span>
              <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto pr-1">
                {events.map((ev) => {
                  const on = hookForm.events.includes(ev.key);
                  return (
                    <button key={ev.id} type="button" onClick={() => setHookForm({ ...hookForm, events: on ? hookForm.events.filter((x) => x !== ev.key) : [...hookForm.events, ev.key] })} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors cursor-pointer whitespace-nowrap ${on ? 'bg-accent-500 text-background-50 border-accent-500' : 'bg-background-50 text-foreground-600 border-background-200/70 hover:bg-background-100'}`}>
                      {on && <i className="ri-check-line"></i>}{ev.key}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}